import moment from "moment";
import {
  BidV2Status,
  CompetitionStageDto,
  NegotiationRoundStatus,
  ParticipationRequestStatus,
  SourcingEventStatus,
  type BuyerParticipationRequestDto,
  type BuyerParticipationRequestsDto,
  type EvalBidDto,
  type EvaluationDto,
  type NegotiationRoundV2Dto,
  type ReceivedBidV2Dto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import {
  isQualificationScheme,
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { timeLeftUntil, type TimeLeft } from "../../../../common/formatting/timeLeft";
import { canSelectWinner } from "../award/canSelectWinner";
import { everyScoreIn } from "../evaluation/evaluationCompleteness";
import { REVEAL_ANCHORS, subjectAnchor } from "../revealAnchor";
import { contractUrl } from "../contractUrl";
import type { EventTab } from "../EventTabs";
import type { EventSubTab } from "../useGoToEventTab";
import type { TranslationKey } from "../qualification/deriveQualificationSteps";
import { effectiveBidDeadline, effectiveRequestDeadline, isEvaluationLocked } from "../qualification/stage";
import { deriveMinimumRequirementReview } from "../minimum-requirements/deriveMinimumRequirementReview";
import { deriveTenderQualification } from "../qualification/tender/deriveTenderQualification";

// Every key this module can emit. They are looked up dynamically in NowPanel, so langen only sees
// them here:
// t('This procurement has been cancelled') t('This procurement is closed')
// t('The competition is open — deadline {{date}}') t('The competition is open')
// t('{{count}} requests received')
// t('{{count}} unanswered questions') t('Answer questions')
// t('{{count}} companies have registered interest')
// t('{{resolved}} of {{total}} suppliers qualified or rejected')
// t('Start the qualification review') t('Continue the qualification review')
// t('Review the qualification of {{supplier}}')
// t('Assess the minimum requirements for {{supplier}}') t('Score {{supplier}}')
// t('The deadline has passed — ready to start the evaluation') t('Begin evaluation')
// t('No bids delivered - nothing to evaluate')
// t('The system is open for applications') t('The system has expired') t('{{count}} applications awaiting assessment')
// t('Shortest deadline: {{count}} working days (FOA § 26-6)') t('Assess applications')
// t('Every request is reviewed — choose who to invite') t('Go to the qualification')
// t('{{reviewed}} of {{total}} tenders assessed against the minimum requirements')
// t('Start the minimum requirements review') t('Continue the minimum requirements review')
// t('The evaluation is complete — ready for award')
// t('Select a winner') t('Award to selected suppliers')
// t('Complete negotiation round {{round}} before awarding') t('Complete the round')
// t('Negotiation round {{round}} is open until {{date}}') t('Go to the round')
// t('{{reviewed}} of {{total}} tenders fully evaluated')
// t('Start the evaluation review') t('Continue the evaluation review')
// t('Ready to complete the evaluation') t('Complete the evaluation') t('Reopen the evaluation')
// t('Send the award letters to start the standstill period') t('Go to the award letters')
// t('Send the letters to all suppliers')
// t('See the award')
// t('The standstill period has expired') t('Confirm award')
// t('The standstill period ends {{date}} ({{timeLeft}})')
// t('The award is confirmed') t('Create contract') t('Go to contract')
// t('The award letters have not been sent yet')
export const NOW_STATE_KEYS = [
  "SCHEME_INTAKE",
  "REQUESTS_OPEN",
  "TENDER_OPEN",
  "REQUEST_QUALIFICATION",
  "REQUEST_SELECTION",
  "TENDER_QUALIFICATION",
  "START_EVALUATION",
  "ROUND_OPEN",
  "MINIMUM_REQUIREMENTS",
  "EVALUATION",
  "ROUND_TO_COMPLETE",
  "READY_TO_AWARD",
  "AWARD_LETTERS_PENDING",
  "STANDSTILL_RUNNING",
  "STANDSTILL_EXPIRED",
  "AWARD_CONFIRMED",
  "CANCELLED",
  "CLOSED",
  "COMPLETED",
] as const;

export type NowStateKey = (typeof NOW_STATE_KEYS)[number];

export type NowCommandTarget =
  | {
    kind: "command";
    command:
      | "beginEvaluation"
      | "completeEvaluation"
      | "reopenEvaluation"
      | "awardEvent"
      | "confirmAward"
      | "sendAwardLetters";
  }
  | { kind: "command"; command: "completeRound"; roundId: string }
  | { kind: "command"; command: "createContract"; bidId: string };

export type NowActionTarget =
  | { kind: "tab"; tab: EventTab; subTab?: EventSubTab; focusBidId?: string; reveal?: string; round?: string }
  | { kind: "url"; url: ReturnType<typeof contractUrl> }
  | NowCommandTarget;

export interface NowAction {
  labelKey: string;
  labelParams?: Record<string, string | number>;
  icon?: "send";
  target: NowActionTarget;
}

export interface NowProgress {
  done: number;
  total: number;
}

export interface NowPanelState {
  key: NowStateKey;
  status: TranslationKey & { dateIso?: string; timeLeft?: TimeLeft };
  detail: (TranslationKey & { text?: string })[];
  progress: NowProgress | null;
  action: NowAction | null;
  secondary?: NowAction | null;
}

export interface NowPanelInput {
  event: SourcingEventV2Dto;
  now: number;
  unansweredQuestions: number;
  participationRequests: BuyerParticipationRequestsDto | null;
  bids: ReceivedBidV2Dto[];
  bidsLoaded: boolean;
  selectedWinnerCount: number;
  evaluation: EvaluationDto | null;
  rounds: readonly NegotiationRoundV2Dto[];
  awardLettersSent: boolean | null;
  awardLettersApproved: boolean | null;
  activeTab: EventTab;
  focusModeEnabled: boolean;
}

const HIDDEN_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Draft,
  SourcingEventStatus.PublicationPending,
  SourcingEventStatus.PublicationFailed,
]);

const tab = (target: EventTab): NowActionTarget => ({ kind: "tab", tab: target });

const seeTheAward: NowAction = { labelKey: "See the award", target: tab("award") };

const TERMINAL_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
]);

/** False where the panel renders nothing or a terminal line, so callers can skip fetching bids. */
export const panelReadsBids = (status: SourcingEventStatus): boolean =>
  !HIDDEN_STATUSES.has(status) && !TERMINAL_STATUSES.has(status);

const publicProcedureOf = (event: SourcingEventV2Dto) =>
  event.eventContext.type === "Public" ? event.eventContext.procedureType : null;

const hasPassed = (iso: string | null | undefined, now: number): boolean =>
  iso != null && now >= Date.parse(iso);

// A revoked award leaves `award` non-null but drops `awardedAt`, so presence alone would report a
// withdrawn decision as a confirmed one (SourcingEventV2Dto.toDto, the revokedDecisions branch).
const isAwarded = (event: SourcingEventV2Dto): boolean => event.award?.awardedAt != null;

const registeredInterest = (event: SourcingEventV2Dto): TranslationKey[] => {
  const count = (event.supplierRegistrations ?? []).length;
  return count > 0 ? [{ key: "{{count}} companies have registered interest", params: { count } }] : [];
};

const unansweredDetail = (count: number): TranslationKey[] =>
  count > 0 ? [{ key: "{{count}} unanswered questions", params: { count } }] : [];

const answerQuestions = (count: number): NowAction | null =>
  count > 0
    ? { labelKey: "Answer questions", target: { kind: "tab", tab: "communication", subTab: "questions" } }
    : null;

const openCompetition = (
  key: NowStateKey,
  deadlineIso: string | null,
  detail: TranslationKey[],
  action: NowAction | null,
): NowPanelState => ({
  key,
  status: deadlineIso
    ? { key: "The competition is open — deadline {{date}}", dateIso: deadlineIso }
    : { key: "The competition is open" },
  detail,
  progress: null,
  action,
});

const resumeLabel = (progress: NowProgress, startKey: string, continueKey: string): string =>
  progress.done === 0 ? startKey : continueKey;

interface ReviewLabels {
  startKey: string;
  continueKey: string;
  focusKey: string;
}

interface ReviewSubject {
  bidId: string;
  supplierName: string;
}

const reviewAction = (
  input: NowPanelInput,
  tab: EventTab,
  progress: NowProgress,
  labels: ReviewLabels,
  next: ReviewSubject | undefined,
  round?: string,
): NowAction => {
  const namesNext = input.activeTab === tab && next != null;
  if (!namesNext) {
    return {
      labelKey: resumeLabel(progress, labels.startKey, labels.continueKey),
      target: { kind: "tab", tab },
    };
  }
  if (!input.focusModeEnabled) {
    return {
      labelKey: resumeLabel(progress, labels.startKey, labels.continueKey),
      target: { kind: "tab", tab, reveal: subjectAnchor(next.bidId) },
    };
  }
  return {
    labelKey: labels.focusKey,
    labelParams: { supplier: next.supplierName },
    target: { kind: "tab", tab, focusBidId: next.bidId, round },
  };
};

export const latestRoundOf = (rounds: readonly NegotiationRoundV2Dto[]): NegotiationRoundV2Dto | null =>
  [...rounds]
    .filter((round) => round.status !== NegotiationRoundStatus.Draft)
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1) ?? null;

const tendersOf = (bids: ReceivedBidV2Dto[], roundId: string | null): ReceivedBidV2Dto[] =>
  bids.filter((bid) => (bid.forNegotiationRound ?? null) === roundId);

const isFullyEvaluated = (bid: EvalBidDto): boolean =>
  bid.cells.filter((cell) => !cell.isAutoComputed).every((cell) => cell.points != null);

const cancelled = (event: SourcingEventV2Dto): NowPanelState => {
  const reason = event.cancellation?.reason?.trim();
  return {
    ...terminal("CANCELLED", "This procurement has been cancelled"),
    detail: reason ? [{ key: "Reason for cancellation", text: reason }] : [],
  };
};

export const deriveNowPanel = (input: NowPanelInput): NowPanelState | null => {
  const { event } = input;
  if (HIDDEN_STATUSES.has(event.status)) return null;

  switch (event.status) {
    case SourcingEventStatus.Cancelled:
      return cancelled(event);
    case SourcingEventStatus.Closed:
      return terminal("CLOSED", "This procurement is closed");
    case SourcingEventStatus.Completed:
      return terminal("COMPLETED", "This procurement is completed");
    case SourcingEventStatus.Published:
      return published(input);
    case SourcingEventStatus.Evaluation:
    case SourcingEventStatus.Evaluated:
      return evaluating(input);
    case SourcingEventStatus.AwardDecision:
      return awardDecision(input);
    case SourcingEventStatus.Awarded:
      return awardConfirmed(input);
    default:
      return null;
  }
};

const terminal = (key: NowStateKey, statusKey: string): NowPanelState => ({
  key,
  status: { key: statusKey },
  detail: [],
  progress: null,
  action: null,
});

const published = (input: NowPanelInput): NowPanelState | null => {
  const { event, now, unansweredQuestions, participationRequests, bids } = input;
  const procedureType = publicProcedureOf(event);

  if (isSchemeProcedure(procedureType)) return schemeIntake(input);

  if (isTwoStageProcedure(procedureType)) {
    switch (event.competitionStage) {
      case CompetitionStageDto.RequestsOpen:
        return openCompetition(
          "REQUESTS_OPEN",
          effectiveRequestDeadline(event),
          [
            { key: "{{count}} requests received", params: { count: participationRequests?.count ?? 0 } },
            ...unansweredDetail(unansweredQuestions),
          ],
          answerQuestions(unansweredQuestions),
        );
      case CompetitionStageDto.Qualification:
        return requestQualification(input);
      default:
        break;
    }
  }

  if (!hasPassed(effectiveBidDeadline(event), now)) {
    return openCompetition(
      "TENDER_OPEN",
      effectiveBidDeadline(event),
      [...registeredInterest(event), ...unansweredDetail(unansweredQuestions)],
      answerQuestions(unansweredQuestions),
    );
  }

  if (isEvaluationLocked(event)) {
    const summary = deriveTenderQualification(event.eventData.selectionCriteria ?? [], tendersOf(bids, null));
    const progress = { done: summary.qualified + summary.rejected, total: summary.rows.length };
    return {
      key: "TENDER_QUALIFICATION",
      status: {
        key: "{{resolved}} of {{total}} suppliers qualified or rejected",
        params: { resolved: progress.done, total: progress.total },
      },
      detail: [],
      progress,
      action: reviewAction(
        input,
        "qualification",
        progress,
        {
          startKey: "Start the qualification review",
          continueKey: "Continue the qualification review",
          focusKey: "Review the qualification of {{supplier}}",
        },
        summary.rows.find((row) => row.state === "PENDING"),
      ),
    };
  }

  const openRound = latestOpenRound(input.rounds);
  if (openRound && !hasPassed(openRound.deadline, now)) return roundOpen(openRound);

  return startEvaluation(input);
};

const roundOpen = (round: NegotiationRoundV2Dto): NowPanelState => ({
  key: "ROUND_OPEN",
  status: {
    key: "Negotiation round {{round}} is open until {{date}}",
    params: { round: round.roundNumber },
    dateIso: round.deadline,
  },
  detail: [],
  progress: null,
  action: { labelKey: "Go to the round", target: tab("request") },
});

// BeginEvaluation.kt lets any published event past its deadline through, deliveries or not.
const startEvaluation = (input: NowPanelInput): NowPanelState => {
  const nothingToEvaluate = input.bidsLoaded && input.bids.length === 0;
  return {
    key: "START_EVALUATION",
    status: {
      key: nothingToEvaluate
        ? "No bids delivered - nothing to evaluate"
        : "The deadline has passed — ready to start the evaluation",
    },
    detail: [],
    progress: null,
    action: nothingToEvaluate
      ? null
      : { labelKey: "Begin evaluation", target: { kind: "command", command: "beginEvaluation" } },
  };
};

const schemeValidTo = (event: SourcingEventV2Dto): string | null => {
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  return publicContext?.dpsScheme?.validTo ?? publicContext?.qualificationScheme?.validTo ?? null;
};

// Forsyningsforskriften § 13-2 clocks admission in calendar months, not in virkedager.
const shortestAssessmentWindow = (
  event: SourcingEventV2Dto,
  awaiting: BuyerParticipationRequestDto[],
): number | null =>
  isQualificationScheme(publicProcedureOf(event))
    ? null
    : awaiting.reduce<number | null>((fewest, request) => {
      const remaining = request.assessmentWorkingDaysRemaining;
      if (remaining == null) return fewest;
      return fewest == null || remaining < fewest ? remaining : fewest;
    }, null);

const schemeIntake = (input: NowPanelInput): NowPanelState => {
  const { event, now, participationRequests } = input;
  const awaiting = (participationRequests?.requests ?? []).filter(
    (request) => request.status === ParticipationRequestStatus.Submitted,
  );

  if (awaiting.length === 0) {
    const expired = hasPassed(schemeValidTo(event), now);
    return {
      key: "SCHEME_INTAKE",
      status: { key: expired ? "The system has expired" : "The system is open for applications" },
      detail: [],
      progress: null,
      action: null,
    };
  }

  const shortest = shortestAssessmentWindow(event, awaiting);
  return {
    key: "SCHEME_INTAKE",
    status: { key: "{{count}} applications awaiting assessment", params: { count: awaiting.length } },
    detail:
      shortest == null
        ? []
        : [{ key: "Shortest deadline: {{count}} working days (FOA § 26-6)", params: { count: shortest } }],
    progress: null,
    action: {
      labelKey: "Assess applications",
      target: { kind: "tab", tab: "dpsSuppliers", reveal: REVEAL_ANCHORS.dpsApplications },
    },
  };
};

const requestQualification = (input: NowPanelInput): NowPanelState => {
  const requests = input.participationRequests?.requests ?? [];
  const unresolved = requests.filter(
    (request) => request.status === ParticipationRequestStatus.Submitted,
  ).length;
  const progress = { done: requests.length - unresolved, total: requests.length };

  if (unresolved === 0 && requests.length > 0) {
    return {
      key: "REQUEST_SELECTION",
      status: { key: "Every request is reviewed — choose who to invite" },
      detail: [],
      progress: null,
      action: {
        labelKey: "Go to the qualification",
        target: { kind: "tab", tab: "qualification", reveal: REVEAL_ANCHORS.invitationSelection },
      },
    };
  }

  return {
    key: "REQUEST_QUALIFICATION",
    status: {
      key: "{{resolved}} of {{total}} suppliers qualified or rejected",
      params: { resolved: progress.done, total: progress.total },
    },
    detail: [],
    progress,
    action: {
      labelKey: resumeLabel(progress, "Start the qualification review", "Continue the qualification review"),
      target: { kind: "tab", tab: "qualification", reveal: REVEAL_ANCHORS.qualificationReview },
    },
  };
};

// canSelectWinner lets an EVALUATED event through with a round still open; AwardSourcingEvent.kt
// rejects that with CannotAwardWithOpenNegotiationRound.
const awardAction = (input: NowPanelInput): NowAction | null => {
  const { event, rounds, selectedWinnerCount } = input;
  const openRound = rounds.some((round) => round.status !== NegotiationRoundStatus.Completed);
  if (selectedWinnerCount === 0 || openRound || !canSelectWinner(event.status, rounds)) return null;
  return { labelKey: "Award to selected suppliers", target: { kind: "command", command: "awardEvent" } };
};

const evaluating = (input: NowPanelInput): NowPanelState => {
  const { event, evaluation, rounds } = input;

  if (isAwarded(event)) return awardConfirmed(input);

  const minimumRequirements = minimumRequirementsReview(input);
  if (minimumRequirements != null) {
    const { progress } = minimumRequirements;
    const roundId = latestRoundOf(rounds)?.id;
    return {
      key: "MINIMUM_REQUIREMENTS",
      status: {
        key: "{{reviewed}} of {{total}} tenders assessed against the minimum requirements",
        params: { reviewed: progress.done, total: progress.total },
      },
      detail: [],
      progress,
      secondary: null,
      action: reviewAction(
        input,
        "minimumRequirements",
        progress,
        {
          startKey: "Start the minimum requirements review",
          continueKey: "Continue the minimum requirements review",
          focusKey: "Assess the minimum requirements for {{supplier}}",
        },
        minimumRequirements.next,
        roundId,
      ),
    };
  }

  const rankable = (evaluation?.bids ?? []).filter((bid) => bid.status !== BidV2Status.Rejected);
  const progress = { done: rankable.filter(isFullyEvaluated).length, total: rankable.length };
  const nothingDelivered = (evaluation?.bids ?? []).length === 0;

  const openRound = latestOpenRound(rounds);
  if (
    openRound &&
    (event.status === SourcingEventStatus.Evaluated || everyScoreIn(evaluation) || nothingDelivered)
  ) {
    return roundToComplete(openRound, input.now);
  }

  if (canSelectWinner(event.status, rounds)) {
    return {
      key: "READY_TO_AWARD",
      status: { key: "The evaluation is complete — ready for award" },
      detail: [],
      progress: null,
      secondary:
        event.status === SourcingEventStatus.Evaluated
          ? { labelKey: "Reopen the evaluation", target: { kind: "command", command: "reopenEvaluation" } }
          : null,
      action:
        awardAction(input) ?? {
          labelKey: "Select a winner",
          target: { kind: "tab", tab: "award", reveal: REVEAL_ANCHORS.winnerSelection },
        },
    };
  }

  if (everyScoreIn(evaluation) && rounds.length === 0) {
    return {
      key: "EVALUATION",
      status: { key: "Ready to complete the evaluation" },
      detail: [],
      progress: null,
      action: {
        labelKey: "Complete the evaluation",
        target: { kind: "command", command: "completeEvaluation" },
      },
    };
  }

  if (nothingDelivered) {
    return {
      key: "EVALUATION",
      status: { key: "Nothing to evaluate yet" },
      detail: [],
      progress: null,
      action: null,
    };
  }

  return {
    key: "EVALUATION",
    status: {
      key: "{{reviewed}} of {{total}} tenders fully evaluated",
      params: { reviewed: progress.done, total: progress.total },
    },
    detail: [],
    progress: progress.total > 0 ? progress : null,
    action: reviewAction(
      input,
      "evaluation",
      progress,
      {
        startKey: "Start the evaluation review",
        continueKey: "Continue the evaluation review",
        focusKey: "Score {{supplier}}",
      },
      // A price-ranked competition scores nothing by hand, so every bid reads as fully evaluated.
      nextToScore(rankable),
      latestRoundOf(rounds)?.id,
    ),
  };
};

const latestOpenRound = (rounds: readonly NegotiationRoundV2Dto[]): NegotiationRoundV2Dto | null =>
  [...rounds]
    .filter((round) => round.status === NegotiationRoundStatus.Published)
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1) ?? null;

const roundToComplete = (round: NegotiationRoundV2Dto, now: number): NowPanelState =>
  hasPassed(round.deadline, now)
    ? {
        key: "ROUND_TO_COMPLETE",
        status: { key: "Complete negotiation round {{round}} before awarding", params: { round: round.roundNumber } },
        detail: [],
        progress: null,
        action: { labelKey: "Complete the round", target: { kind: "command", command: "completeRound", roundId: round.id } },
      }
    : {
        key: "ROUND_TO_COMPLETE",
        status: {
          key: "Negotiation round {{round}} is open until {{date}}",
          params: { round: round.roundNumber },
          dateIso: round.deadline,
        },
        detail: [],
        progress: null,
        action: { labelKey: "Go to the round", target: tab("request") },
      };

const nextToScore = (rankable: EvalBidDto[]): ReviewSubject | undefined => {
  const bid = rankable.find((candidate) => !isFullyEvaluated(candidate));
  return bid && { bidId: bid.bidId, supplierName: bid.supplierName };
};

const minimumRequirementsReview = (
  input: NowPanelInput,
): { progress: NowProgress; next: ReviewSubject | undefined } | null => {
  const requirements = input.event.eventData.minimumRequirements ?? [];
  if (requirements.length === 0) return null;
  const roundId = latestRoundOf(input.rounds)?.id ?? null;
  const summary = deriveMinimumRequirementReview(requirements, tendersOf(input.bids, roundId));
  if (summary.rows.length === 0 || summary.isResolved) return null;
  return {
    progress: { done: summary.assessed, total: summary.assessable },
    next: summary.rows.find((row) => row.state === "PENDING"),
  };
};

const awardLettersAction = (input: NowPanelInput): NowAction =>
  input.awardLettersApproved === true
    ? {
      labelKey: "Send the letters to all suppliers",
      icon: "send",
      target: { kind: "command", command: "sendAwardLetters" },
    }
    : {
      labelKey: "Go to the award letters",
      target: { kind: "tab", tab: "award", reveal: REVEAL_ANCHORS.awardLetters },
    };

const awardDecision = (input: NowPanelInput): NowPanelState => {
  const expiresAt = input.event.award?.standstillExpiresAt;

  if (expiresAt == null) {
    return {
      key: "AWARD_LETTERS_PENDING",
      status: { key: "Send the award letters to start the standstill period" },
      detail: [],
      progress: null,
      action: awardLettersAction(input),
    };
  }

  if (hasPassed(expiresAt, input.now)) {
    return {
      key: "STANDSTILL_EXPIRED",
      status: { key: "The standstill period has expired" },
      detail: [],
      progress: null,
      action: { labelKey: "Confirm award", target: { kind: "command", command: "confirmAward" } },
    };
  }

  return {
    key: "STANDSTILL_RUNNING",
    status: {
      key: "The standstill period ends {{date}} ({{timeLeft}})",
      dateIso: expiresAt,
      timeLeft: timeLeftUntil(expiresAt, moment(input.now)),
    },
    detail: [],
    progress: null,
    action: seeTheAward,
  };
};

const awardConfirmedAction = (event: SourcingEventV2Dto, bids: ReceivedBidV2Dto[]): NowAction => {
  const contractIds = event.award?.contractIds ?? [];
  const winningBidIds = event.award?.winningBidIds ?? [];

  if (contractIds.length === 0) {
    const onlyWinner =
      event.status === SourcingEventStatus.Awarded && winningBidIds.length === 1
        ? bids.find((bid) => bid.id === winningBidIds[0])
        : undefined;
    return onlyWinner
      ? {
        labelKey: "Create contract",
        target: { kind: "command", command: "createContract", bidId: onlyWinner.id },
      }
      : seeTheAward;
  }

  // contractUrl routes on the contract's state, which only the bid carries.
  const onlyContract =
    contractIds.length === 1 ? bids.find((bid) => bid.contractId === contractIds[0]) : undefined;
  return onlyContract
    ? {
      labelKey: "Go to contract",
      target: { kind: "url", url: contractUrl(contractIds[0], onlyContract.contractState, event.id) },
    }
    : seeTheAward;
};

const awardConfirmed = (input: NowPanelInput): NowPanelState => {
  if (input.awardLettersSent === false) {
    return {
      key: "AWARD_LETTERS_PENDING",
      status: { key: "The award letters have not been sent yet" },
      detail: [],
      progress: null,
      action: awardLettersAction(input),
    };
  }

  return {
    key: "AWARD_CONFIRMED",
    status: { key: "The award is confirmed" },
    detail: [],
    progress: null,
    action: awardConfirmedAction(input.event, input.bids),
  };
};
